import { Box, MenuItem, SelectChangeEvent, ThemeProvider, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getLoggedCompany, reset as resetCompany } from 'app/entities/company/company.reducer';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { ActivatedSwitch } from 'app/shared/components/activated-switch/ActivatedSwitch';
import { AutoCompleteWithoutPagination } from 'app/shared/components/autocomplete-without-pagination/AutoCompleteWithoutPagination';
import { ImageInput } from 'app/shared/components/image-input/ImageInput';
import { SelectDefault } from 'app/shared/components/select-default/SelectDefault';
import SelectPaginate from 'app/shared/components/select-paginate/SelectPaginate';
import { BoxForm, FormControlDefault, MenuItemStyled, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { RegisterLayout } from 'app/shared/layout/register/RegisterLayout';
import { RegisterTitle } from 'app/shared/layout/register/RegisterTitle';
import { CategoryVehicleModel } from 'app/shared/model/enumerations/category-vehicle-model.model';
import { IMaintenanceModel } from 'app/shared/model/maintenance-model.model';
import { ITruckImplement } from 'app/shared/model/truck-implement.model';
import { IVehicleModel } from 'app/shared/model/vehicle-model.model';
import { convertDateFromServer, convertDateTimeToServer, convertYearFromServer, getYears } from 'app/shared/util/date-utils';
import React, { useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getEntitiesByCategories } from '../vehicle-model/vehicle-model.reducer';
import { createEntity, getEntity, reset, updateEntity } from './truck-implement.reducer';

export const TruckImplementUpdate = (props: RouteComponentProps<{ id: string }>) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const truckImplementEntity = useAppSelector(state => state.truckImplement.entity);
  const loading = useAppSelector(state => state.truckImplement.loading);
  const companyEntity = useAppSelector(state => state.company.entity);

  const [maintenanceModel, setMaintenanceModel] = useState<IMaintenanceModel>(null);
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const [checked, setChecked] = useState(true);
  const [category, setCategory] = useState<CategoryVehicleModel | string>(null);
  const [options, setOptions] = useState<IVehicleModel[]>([]);
  const [formData, setFormData] = useState({
    id: null,
    licensePlate: '',
    image: '',
    imageContentType: '',
    description: '',
    chassisNumber: '',
    fabricationYear: '',
    modelYear: '',
    purchaseDate: null,
    activated: true,
    vehicleModel: null,
    maintenanceModel: null,
    company: null,
  });
  const [years, setYears] = useState([]);
  const inputRef = React.useRef(null);

  const isAllAdmins = isAdmin || isManufacturerAdmin || isTransporterAdmin || isRepresentativeAdmin;

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(props.match.params.id));
    }
  }, []);

  useEffect(() => {
    if (!isAdmin && !isRepresentativeAdmin) {
      dispatch(getLoggedCompany());
    } else {
      dispatch(resetCompany());
    }
  }, [isAdmin, isRepresentativeAdmin]);

  useEffect(() => {
    return setYears(getYears());
  }, []);

  useEffect(() => {
    setChecked(formData['activated']);
  }, [formData]);

  useEffect(() => {
    if (truckImplementEntity?.maintenanceModel && !isNew) {
      setMaintenanceModel(truckImplementEntity?.maintenanceModel);
    }
  }, [truckImplementEntity]);

  useEffect(() => {
    if (!isNew && truckImplementEntity) {
      setFormData({
        id: truckImplementEntity?.id,
        licensePlate: truckImplementEntity?.licensePlate,
        image: truckImplementEntity?.image,
        imageContentType: truckImplementEntity?.imageContentType,
        description: truckImplementEntity?.description,
        chassisNumber: truckImplementEntity?.chassisNumber,
        fabricationYear: convertYearFromServer(truckImplementEntity.fabricationYear),
        modelYear: convertYearFromServer(truckImplementEntity.modelYear),
        purchaseDate: convertDateFromServer(truckImplementEntity.purchaseDate),
        activated: truckImplementEntity?.activated,
        vehicleModel: truckImplementEntity?.vehicleModel,
        maintenanceModel: truckImplementEntity?.maintenanceModel,
        company: truckImplementEntity?.company,
      });
    }
  }, [truckImplementEntity]);

  const handleYear = (name, event) => {
    handleInputChange(name, `${event}`);
  };

  const handleInputChange = (fieldName, value) => {
    setFormData({ ...formData, [fieldName]: value });
  };

  const handleClose = assetId => {
    dispatch(reset());
    props.history.push(`/dashboards/truck-implement/${assetId}`);
  };

  const saveEntity = values => {
    values.fabricationYear = convertDateTimeToServer(values.fabricationYear);
    values.modelYear = convertDateTimeToServer(values.modelYear);
    values.purchaseDate = convertDateTimeToServer(values.purchaseDate);

    const entity = {
      ...(isNew ? {} : truckImplementEntity),
      ...values,
      company: values.company ?? companyEntity,
      vehicleModel: values.vehicleModel,
      maintenanceModel: values?.maintenanceModel ?? maintenanceModel,
      image: values.image !== '' ? values.image : null,
      imageContentType: values.imageContentType !== '' ? values.imageContentType : null,
      truckGroup: isNew ? null : (values.truckGroup ?? truckImplementEntity?.truckGroup ?? null),
    };

    if (!entity.vehicleModel) {
      return toast.error('Necessário selecionar um modelo do ativo.');
    }

    if (!entity.company?.id) {
      return toast.error('Necessário selecionar uma empresa.');
    }

    const handleEntityDispatch = async (entityParam: ITruckImplement, isNewParam: boolean) => {
      try {
        const action = isNewParam ? createEntity(entityParam) : updateEntity(entityParam);
        const { payload }: { payload: { data?: { id?: any } } } = await dispatch(action);
        const entityId = isNewParam ? payload?.data?.id : entityParam.id;

        if (entityId) {
          handleClose(entityId);
        }
      } catch (error) {
        console.error('Error when dealing with entity:', error);
      }
    };

    handleEntityDispatch(entity, isNew);
  };

  const handleCategory = (event: SelectChangeEvent) => {
    setCategory(event.target.value);
    setFormData({ ...formData, vehicleModel: null });
  };

  useEffect(() => {
    const fetchModels = async () => {
      const onlyCategory = category || truckImplementEntity?.vehicleModel?.category;

      try {
        const response = await dispatch(getEntitiesByCategories(onlyCategory)).unwrap();
        setOptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch items', error);
      }
    };

    if (category || truckImplementEntity?.vehicleModel?.category) {
      fetchModels();
    }
  }, [category, truckImplementEntity]);

  const handleSubmit = () => {
    event.preventDefault();
    saveEntity(formData);
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <RegisterLayout>
          <Box sx={{ width: { xs: '95%', sm: '70%', lg: '50%' } }} justifyContent={'center'}>
            <RegisterTitle isNew={isNew} contentKey={'fleetSenseApp.truckImplement'} />
            <Box>
              {loading ? (
                <Box>
                  <Typography>Loading...</Typography>
                </Box>
              ) : (
                <Box margin={'0 auto'} width={'100%'}>
                  <BoxForm onSubmit={handleSubmit}>
                    {!isNew && (
                      <FormControlDefault sx={{ display: 'none' }}>
                        <TypographyForms>{translate('global.field.id')}</TypographyForms>
                        <TextFieldForms value={formData['id']} id={'truck-implement-id'} name={'id'} />
                      </FormControlDefault>
                    )}
                    <ImageInput image={formData['image']} imageContentType={formData['imageContentType']} handleInputChange={handleInputChange}></ImageInput>
                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.truckImplement.licensePlate')}</TypographyForms>
                      <TextFieldForms
                        value={formData['licensePlate']}
                        id={'truck-implement-license-plate'}
                        data-cy={'licensePlate'}
                        name={'licensePlate'}
                        required={true}
                        onChange={e => handleInputChange('licensePlate', e.target.value.replace(/,/g, ''))}
                      />
                    </FormControlDefault>
                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.truckImplement.description')}</TypographyForms>
                      <TextFieldForms
                        value={formData['description']}
                        id={'truck-implement-description'}
                        data-cy={'description'}
                        name={'description'}
                        onChange={e => handleInputChange('description', e.target.value)}
                      />
                    </FormControlDefault>
                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.truckImplement.chassisNumber')}</TypographyForms>
                      <TextFieldForms
                        value={formData['chassisNumber']}
                        id={'truck-implement-chassisNumber'}
                        data-cy={'chassisNumber'}
                        name={'chassisNumber'}
                        required={true}
                        onChange={e => handleInputChange('chassisNumber', e.target.value.replace(/,/g, ''))}
                      />
                    </FormControlDefault>
                    <FormControlDefault>
                      <TypographyForms>{translate('fleetSenseApp.truckImplement.fabricationYear')}</TypographyForms>
                      <TextFieldForms
                        value={formData['fabricationYear'] || ''}
                        onChange={event => handleYear('fabricationYear', event.target.value)}
                        select
                        name={'fabricationYear'}
                        data-cy={'fabricationYear'}
                        required={false}
                      >
                        {isNew && <MenuItemStyled value="" key="0" />}
                        {years &&
                          years.map(year => (
                            <MenuItemStyled value={year} key={year}>
                              {year}
                            </MenuItemStyled>
                          ))}
                      </TextFieldForms>
                    </FormControlDefault>
                    <FormControlDefault>
                      <TypographyForms>{translate('fleetSenseApp.truckImplement.modelYear')}</TypographyForms>
                      <TextFieldForms
                        value={formData['modelYear'] || ''}
                        onChange={event => handleYear('modelYear', event.target.value)}
                        select
                        name={'modelYear'}
                        data-cy={'modelYear'}
                        required={false}
                      >
                        {isNew && <MenuItem value="" key="0" />}
                        {years &&
                          years.map(year => (
                            <MenuItemStyled value={year} key={year}>
                              {year}
                            </MenuItemStyled>
                          ))}
                      </TextFieldForms>
                    </FormControlDefault>
                    <FormControlDefault>
                      <TypographyForms>{translate('fleetSenseApp.truckImplement.purchaseDate')}</TypographyForms>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          inputFormat={'DD/MM/YYYY'}
                          value={formData['purchaseDate'] || null}
                          onChange={date => handleInputChange('purchaseDate', date)}
                          renderInput={params => <TextFieldForms {...params} inputRef={inputRef} />}
                          PopperProps={{
                            anchorEl: inputRef?.current?.nextSibling,
                            placement: 'top-start',
                          }}
                        />
                      </LocalizationProvider>
                    </FormControlDefault>
                    {isAllAdmins && (
                      <FormControlDefault>
                        <TypographyForms>{translate('fleetSenseApp.truckImplement.category')}</TypographyForms>
                        <SelectDefault value={formData.vehicleModel?.category || category} onChange={handleCategory}>
                          {Object.entries(CategoryVehicleModel).map(([key, value]) => (
                            <MenuItem value={value} key={key}>
                              {translate(`fleetSenseApp.CategoryVehicleModel.${value}`)}
                            </MenuItem>
                          ))}
                        </SelectDefault>
                      </FormControlDefault>
                    )}
                    {isAllAdmins && (
                      <FormControlDefault>
                        <TypographyForms>{translate('fleetSenseApp.truckImplement.vehicleModel')}</TypographyForms>
                        <AutoCompleteWithoutPagination
                          onChange={(_, newValue) => handleInputChange('vehicleModel', newValue ? newValue : null)}
                          value={formData['vehicleModel'] || null}
                          options={options}
                          getOptionLabel={option => option.name || ''}
                        />
                      </FormControlDefault>
                    )}
                    {isAllAdmins && (
                      <FormControlDefault>
                        <TypographyForms>{translate('fleetSenseApp.vehicleModel.subcategory')}</TypographyForms>
                        <TextFieldForms
                          value={
                            formData['vehicleModel']?.subcategory
                              ? translate(`fleetSenseApp.subcategoryVehicleModel.${formData['vehicleModel'].subcategory}`)
                              : ''
                          }
                          id={'truck-implement-subcategory'}
                          data-cy={'subcategory'}
                          name={'subcategory'}
                          disabled
                        />
                      </FormControlDefault>
                    )}
                    {isAllAdmins && (
                      <FormControlDefault>
                        <SelectPaginate
                          label={translate('fleetSenseApp.truckImplement.maintenanceModel')}
                          apiUrl={'api/maintenance-models'}
                          onChange={value => handleInputChange('maintenanceModel', value)}
                          value={formData['maintenanceModel']}
                          searchName="nameSearch"
                          valueName="name"
                          icon="sort-down"
                          isRequired={false}
                          filterStyle={true}
                        />
                      </FormControlDefault>
                    )}
                    {(isAdmin || isRepresentativeAdmin) && (
                      <FormControlDefault>
                        <SelectPaginate
                          label={translate('fleetSenseApp.userCustom.company')}
                          apiUrl={'api/companies/new-truck-implement'}
                          onChange={value => handleInputChange('company', value)}
                          value={formData['company']}
                          valueName="name"
                          icon="sort-down"
                          isRequired={true}
                          filterStyle={true}
                          sortQuery={isRepresentativeAdmin ? 'targetCompany.name' : 'name'}
                        />
                      </FormControlDefault>
                    )}
                    {isAllAdmins && <ActivatedSwitch checked={checked} setChecked={setChecked} handleInputChange={handleInputChange} />}
                    <RegisterButton />
                  </BoxForm>
                </Box>
              )}
            </Box>
          </Box>
        </RegisterLayout>
      </ThemeProvider>
    </>
  );
};
